import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CSVLink } from 'react-csv';
import Link from 'react-csv/components/Link';
import styled from 'styled-components';
import { fetchCampaignContributionCSVExport } from 'api/IterableGoalsAPI';
import { displayDate, FILENAME_DATE_FORMAT, getNewDate } from 'utils/date';
import { addPositiveToast, addNegativeToast, errorIdentifier } from 'modules/toastContainer';
import { CampaignContributionCSVExportQuery } from '../types';
import { getTableColumns } from '../utils';
import { useGoalsTranslations } from './useTranslations';

export const HiddenCSVLink = styled(CSVLink)`
  display: none;
`;

type LinkComponentProps = {
  data?: string;
  filename?: string;
};

const CSVLinkWrapper = ({ data, filename }: LinkComponentProps) => {
  const linkRef = useRef<Link & { link: HTMLAnchorElement }>(null);

  useEffect(() => {
    if (linkRef?.current?.link?.click && data) {
      linkRef.current.link.click();
    }
  }, [data, linkRef.current]);

  return <HiddenCSVLink filename={filename} data={data || ''} ref={linkRef} />;
};

export const useCampaignContributionCSVExport = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [linkProps, setLinkProps] = useState<LinkComponentProps>({});
  const { t } = useGoalsTranslations();

  const columns = getTableColumns(t);
  const staticIdColumn = t('labels.campaignContributions.tableHeader.campaignId');
  const translatedColumnHeadings = [staticIdColumn, ...columns.map(({ name }) => name)].join(',');

  const exportCSVHandler = async (queryData: CampaignContributionCSVExportQuery) => {
    setLoading(true);

    try {
      const { data } = await fetchCampaignContributionCSVExport(queryData);
      const successMessage = t('labels.campaignContributions.export.successMessage');
      const dataSplitInRows = data.split('\n');

      dataSplitInRows.splice(0, 1, translatedColumnHeadings);

      const csvData = dataSplitInRows.join('\n');
      const filenameDatetime = displayDate(getNewDate(), FILENAME_DATE_FORMAT);
      const filename = `iterable_goals_campaign_contribution_${filenameDatetime}.csv`;

      setLinkProps({ data: csvData, filename });
      dispatch(addPositiveToast(successMessage));
    } catch (error) {
      const errorMessage = t('labels.campaignContributions.export.errorMessage');
      dispatch(
        addNegativeToast(
          errorMessage,
          errorIdentifier({
            source: 'useCampaignContributionCSVExport',
            error,
            additional: queryData,
          }),
        ),
      );
    } finally {
      setLoading(false);
      setLinkProps({});
    }
  };

  return {
    exportCSVHandler,
    linkProps,
    loading,
    CSVLinkWrapper,
  };
};
